import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import debounce from 'lodash-es/debounce'
import React, { useCallback, useEffect, useState } from 'react'
import { Dialogs, useDialogs } from '../../Contexts/DialogsContext'
import { useGlobalContext } from '../../Contexts/GlobalContext'
import { HintBonusIcon } from '../../DailyBonus/assets/HintBonusIcon'
import { BonusType } from '../../DailyBonus/bonusTypes'
import { getBonusAdditionalInfo } from '../../DailyBonus/walletAPI'
import SocialLogin, { SocialType } from '../../SocialLogin/SocialLogin'
import { LoginState } from '../../globalHeader/GlobalHeader'
import {
    isEmailTaken,
    loginSocial,
    signup,
    upgradeGuest,
    upgradeGuestSocial,
    validateUsername,
} from '../../globalHeader/functions/ams-functions'
import { analyticsManager } from '../../globalHeader/services/analytics/AnalyticsManager'
import { reportSharedError } from '../../utils/errorReporting'
import BaseDialog from '../Common/BaseDialog'
import CountrySelect from '../Common/CountrySelect'
import { ValidationState } from '../Common/types'
import { validatePassword } from '../../utils/passwordValidation'
import HidePasswordIcon from '../Icons/hidePassword'
import ShowPasswordIcon from '../Icons/showPassword'
import UserIcon from '../Icons/user'
import { ErrorAction } from './Error'
import SignIn from './SignIn'

interface SignUpProps {
    isSocialSignup?: boolean
}

const SignUp: React.FC<SignUpProps> = ({ isSocialSignup = false }) => {
    const {
        env,
        websiteURL,
        onAuthClose,
        isGuest,
        setUserData,
        onLoginStateChange,
        socialData,
        onErrorHandler,
        onLoginSuccessful,
        userType,
    } = useGlobalContext()
    const { registerDialog, unregisterDialog } = useDialogs()
    const [isFirstPageValid, setIsFirstPageValid] = useState(false)
    const [isSecondPageValid, setIsSecondPageValid] = useState(false)
    const [email, setEmail] = useState('')
    const [isEmailValid, setIsEmailValid] = useState<boolean | undefined>(undefined)
    const [isUsernameValid, setIsUsernameValid] = useState<boolean | undefined>(undefined)
    const [isPasswordValid, setIsPasswordValid] = useState<boolean | undefined>(undefined)
    const [doPasswordsMatch, setDoPasswordsMatch] = useState<boolean | undefined>(undefined)
    const [acceptTerms, setAcceptTerms] = useState(false)
    const [acceptEmailSubscribe, setAcceptEmailSubscribe] = useState(true)
    const [password, setPassword] = useState('')
    const [confirmationPassword, setConfirmationPassword] = useState('')
    const [username, setUsername] = useState('')
    const [country, setCountry] = useState('')
    const [showPassword, setShowPassword] = useState(false)
    const [showConfirmationPassword, setShowConfirmationPassword] = useState(false)
    const [usernameCheckState, setUsernameCheckState] = useState(ValidationState.WAITING)
    const [emailCheckState, setEmailCheckState] = useState(ValidationState.WAITING)
    const [currentPage, setCurrentPage] = useState(isSocialSignup ? 1 : 0)
    const [emailErrorMessage, setEmailErrorMessage] = useState('')
    const [usernameErrorMessage, setUsernameErrorMessage] = useState('')
    const [passwordErrorMessage, setPasswordErrorMessage] = useState('')
    const [tooShort, setTooShort] = useState(false)
    const [tooLong, setTooLong] = useState(false)
    const [invalidChars, setInvalidChars] = useState(false)
    const [containsTitle, setContainsTitle] = useState(false)
    const [amount, setAmount] = useState(0)
    const [isCompletingSignup, setIsCompletingSignup] = useState(false)

    // -------------------------------------------------------------- Set Hints amount -----------------

    // Also awaited right after account creation: a wallet failure must not block the success page
    const updateHintsAmount = async () => {
        try {
            const result = await getBonusAdditionalInfo(BonusType.INITIAL_BONUS_HINT)
            const initialAmountKey = result.findIndex((item) => item.userType === userType)
            const nextInitialAmount = result[initialAmountKey + 1]
            setAmount(nextInitialAmount?.amount || 0)
        } catch (error: any) {
            // no Sentry dependency in this package; the console call is kept as a breadcrumb by the host app
            console.warn('[SignUp] hint bonus info unavailable:', error?.error?.code, error?.status)
            setAmount(0)
        }
    }

    useEffect(() => {
        updateHintsAmount()
    }, [])

    // -------------------------------------------------------------- Error message handling -----------------

    useEffect(() => {
        if (emailCheckState && isEmailValid) {
            switch (emailCheckState) {
                case ValidationState.FREE:
                    setEmailErrorMessage('Email valid')
                    break
                case ValidationState.TAKEN:
                    setEmailErrorMessage('Email already exists')
                    break
                case ValidationState.LOADING:
                case ValidationState.WAITING:
                    setEmailErrorMessage('Checking...')
                    break
                case ValidationState.NOT_ALLOWED:
                default:
                    setEmailErrorMessage('')
                    break
            }
        }
        if (!isEmailValid && isEmailValid !== undefined) {
            setEmailErrorMessage('E-mail not valid')
        }
    }, [emailCheckState, isEmailValid])

    useEffect(() => {
        if (usernameCheckState && !tooLong && !tooShort && !containsTitle && !invalidChars) {
            switch (usernameCheckState) {
                case ValidationState.FREE:
                    setUsernameErrorMessage('Username available')
                    break
                case ValidationState.TAKEN:
                    setUsernameErrorMessage('Username not available')
                    break
                case ValidationState.NOT_ALLOWED:
                    setUsernameErrorMessage('Username can not be used')
                    break
                case ValidationState.LOADING:
                    setUsernameErrorMessage('Checking...')
                    break
                case ValidationState.WAITING:
                default:
                    setUsernameErrorMessage('')
                    break
            }
        }
        if (!isUsernameValid && isUsernameValid !== undefined && tooShort) {
            setUsernameErrorMessage('Username too short')
        }
        if (!isUsernameValid && isUsernameValid !== undefined && containsTitle) {
            setUsernameErrorMessage('Titles not permitted')
        }
        if (!isUsernameValid && isUsernameValid !== undefined && tooLong) {
            setUsernameErrorMessage('Username must be a maximum of 15 characters')
        }
        if (!isUsernameValid && isUsernameValid !== undefined && invalidChars) {
            setUsernameErrorMessage('Username can only contain letters and numbers')
        }
    }, [usernameCheckState, tooLong, tooShort, containsTitle, invalidChars, isUsernameValid])

    //check if first page is valid
    useEffect(() => {
        if (
            isEmailValid &&
            isPasswordValid &&
            doPasswordsMatch &&
            acceptTerms &&
            emailCheckState === ValidationState.FREE
        ) {
            setIsFirstPageValid(true)
        } else {
            setIsFirstPageValid(false)
        }
    }, [isEmailValid, isPasswordValid, doPasswordsMatch, username, country, acceptTerms, emailCheckState])

    useEffect(() => {
        if (isUsernameValid && usernameCheckState === ValidationState.FREE) {
            setIsSecondPageValid(true)
        } else {
            setIsSecondPageValid(false)
        }
    }, [isUsernameValid, country, usernameCheckState])

    // ---------------------------------------------------------------- Email validation -----------------

    const debouncedEmailCheck = useCallback(
        debounce(async (email) => {
            const result = await isEmailTaken(email)
            setEmailCheckState(result ? ValidationState.TAKEN : ValidationState.FREE)
        }, 500),
        [],
    )

    const emailChangeHandler = async (email: string) => {
        const current = email
        setEmail(current)
        const regex =
            env === 'prod'
                ? /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                : /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
        if (current.length < 3 || !regex.test(current)) {
            setIsEmailValid(false)
            return
        }
        setEmailCheckState(ValidationState.LOADING)
        setIsEmailValid(true)
        await debouncedEmailCheck(current)
    }

    // -------------------------------------------------------------- Password validation -----------------

    const onPasswordChange = (password: string) => {
        const result = validatePassword(password)
        setIsPasswordValid(result.isValid)
        setPasswordErrorMessage(result.errorMessage)
    }

    const debouncedPasswordChange = useCallback(debounce(onPasswordChange, 500), [])

    useEffect(() => {
        if (password === '') {
            setIsPasswordValid(undefined)
            setPasswordErrorMessage('')
            return
        }

        const timer = setTimeout(() => {
            debouncedPasswordChange(password)
        }, 500)

        return () => {
            clearTimeout(timer)
        }
    }, [password, debouncedPasswordChange])

    // -------------------------------------------------------------- Confirm password validation -----------------

    const onConfirmationPasswordChange = (confirmationPassword: string, password: string) => {
        setDoPasswordsMatch(confirmationPassword === password)
    }

    const debouncedConfirmationPasswordChange = useCallback(debounce(onConfirmationPasswordChange, 500), [])

    useEffect(() => {
        if (confirmationPassword === '' || password === '') {
            setDoPasswordsMatch(undefined)
            return
        }

        const timer = setTimeout(() => {
            debouncedConfirmationPasswordChange(confirmationPassword, password)
        }, 500)

        return () => {
            clearTimeout(timer)
        }
    }, [confirmationPassword, password, debouncedConfirmationPasswordChange])

    const debouncedUsernameCheck = useCallback(
        debounce(async (email) => {
            const result = await validateUsername(email)
            setUsernameCheckState(result)
        }, 500),
        [],
    )

    const nameChangeHandler = async (current: string) => {
        setUsername(current)
        if (current.length < 5) {
            setIsUsernameValid(false)
            setTooShort(true)
            return
        }
        if (current.length > 15) {
            setIsUsernameValid(false)
            setTooLong(true)
            return
        }

        if (!/^[a-zA-Z0-9](?:[a-zA-Z0-9]*[-._]?[a-zA-Z0-9])+$/.test(current)) {
            setIsUsernameValid(false)
            setInvalidChars(true)
            return
        }
        const titles = ['GM', 'IM', 'FM', 'WIM', 'WGM', 'CM', 'NM', 'WFM', 'WCM']
        const titleRegex = new RegExp(`^(${titles.join('|')})`)
        const usernameRegex = new RegExp(`(${titles.join('|')})$`)

        if (titleRegex.test(current) || usernameRegex.test(current)) {
            setIsUsernameValid(false)
            setContainsTitle(true)
            return
        }
        setContainsTitle(false)
        setTooShort(false)
        setTooLong(false)
        setInvalidChars(false)
        setUsernameCheckState(ValidationState.LOADING)
        setIsUsernameValid(true)
        await debouncedUsernameCheck(current)
    }

    // -------------------------------------------------------------- Form submission -----------------

    const onCloseHandler = () => {
        if (currentPage === 2) {
            onThirdPageSubmit()
        } else {
            onAuthClose()
        }
    }

    const onFirstPageSubmit = () => {
        if (isFirstPageValid) {
            setCurrentPage(1)
        }
    }

    const onSecondPageSubmit = async () => {
        try {
            if (isSecondPageValid && !isCompletingSignup) {
                setIsCompletingSignup(true)
                let data: any = null
                const over12 = true

                if (isSocialSignup) {
                    // Handle social signup
                    let socialToken: string = ''
                    let accessToken: string = ''
                    switch (socialData.socialType) {
                        case SocialType.APPLE:
                            socialToken = socialData.authorization.code
                            accessToken = socialData.socialToken
                            break
                        case SocialType.FACEBOOK:
                            socialToken = socialData.authResponse.accessToken
                            accessToken = socialData.authResponse.signedRequest
                            break
                        case SocialType.GOOGLE:
                            socialToken = socialData.credential
                            accessToken = socialData.clientId
                            break
                        default:
                            break
                    }

                    const emailSubscribe = true
                    if (isGuest) {
                        data = await upgradeGuestSocial(
                            socialToken,
                            accessToken,
                            socialData.socialType,
                            username,
                            country,
                            over12,
                            emailSubscribe,
                        )
                    } else {
                        data = await loginSocial(
                            socialToken,
                            accessToken,
                            socialData.socialType,
                            username,
                            country,
                            over12,
                            emailSubscribe,
                        )
                    }

                    if (data?.error) {
                        onErrorHandler(ErrorAction.SOCIAL_LOGIN)
                        setIsCompletingSignup(false)
                        return
                    } else {
                        analyticsManager.dispatchEvent('completeSignup', {
                            country,
                            authType:
                                socialData.socialType === SocialType.APPLE
                                    ? 'apple'
                                    : socialData.socialType === SocialType.FACEBOOK
                                      ? 'facebook'
                                      : 'google',
                            userId: data.user.id,
                            userName: data.user.user_name,
                        })
                        await updateHintsAmount()
                        setCurrentPage(2)
                        setIsCompletingSignup(false)
                        return
                    }
                } else {
                    // Handle regular signup
                    if (isGuest) {
                        data = await upgradeGuest(email, password, username, country, over12, acceptEmailSubscribe)
                        // setLoginState(LoginState.NOT_LOGGED_IN)
                        setUserData(null)
                        onLoginStateChange({
                            state: LoginState.INITIALIZING,
                            userData: null,
                            token: '',
                        })
                    } else {
                        data = await signup(email, password, username, country, over12, acceptEmailSubscribe)
                    }

                    if (!data?.error) {
                        analyticsManager.dispatchEvent('completeSignup', {
                            country,
                            authType: 'email/user_name',
                            userId: data.user.id,
                            userName: data.user.user_name,
                        })
                        await updateHintsAmount()
                        setCurrentPage(2)
                    }
                    setIsCompletingSignup(false)
                }
            }
        } catch (e) {
            reportSharedError('Signup submit failed', e, { flow: 'signup' })
            setIsCompletingSignup(false)
        }
    }

    const onThirdPageSubmit = () => {
        // ensureLoginCallback()
        // unregisterDialog(Dialogs.SIGNUP)
        onLoginSuccessful()
    }

    const pages = {
        0: {
            title: 'Create your free account',
            content: (
                <>
                    <TextField
                        label="Enter your email:"
                        margin="normal"
                        type="email"
                        autoComplete="email"
                        value={email}
                        onChange={(e) => emailChangeHandler(e.currentTarget.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" sx={{ px: 1, py: 2.7 }}>
                                    <IconButton edge="end" disabled>
                                        <UserIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        error={isEmailValid !== undefined && !isEmailValid}
                        helperText={emailErrorMessage}
                        FormHelperTextProps={{
                            sx:
                                emailCheckState === ValidationState.FREE
                                    ? { color: 'success.main' }
                                    : emailCheckState === ValidationState.LOADING
                                      ? { color: 'text.primary' }
                                      : { color: 'error.main' },
                        }}
                    />
                    <TextField
                        label="Enter your password:"
                        margin="normal"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={password}
                        onChange={(e) => setPassword(e.currentTarget.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" sx={{ px: 1, py: 2.7 }}>
                                    <IconButton edge="end" onClick={() => setShowPassword(!showPassword)}>
                                        {showPassword ? <HidePasswordIcon /> : <ShowPasswordIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        error={!isPasswordValid && isPasswordValid !== undefined}
                        helperText={
                            !isPasswordValid &&
                            isPasswordValid !== undefined &&
                            passwordErrorMessage
                        }
                    />
                    <TextField
                        label="Confirm your password:"
                        margin="normal"
                        type={showConfirmationPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={confirmationPassword}
                        onChange={(e) => setConfirmationPassword(e.currentTarget.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" sx={{ px: 1, py: 2.7 }}>
                                    <IconButton
                                        edge="end"
                                        onClick={() => setShowConfirmationPassword(!showConfirmationPassword)}
                                    >
                                        {showConfirmationPassword ? <HidePasswordIcon /> : <ShowPasswordIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        error={!doPasswordsMatch && doPasswordsMatch !== undefined}
                        helperText={!doPasswordsMatch && doPasswordsMatch !== undefined && 'Passwords do not match'}
                    />
                    <SocialLogin title="Or Sign Up With:" />
                    <Stack sx={{ alignSelf: 'flex-start' }}>
                        <FormControlLabel
                            value="end"
                            onChange={() => setAcceptEmailSubscribe(!acceptEmailSubscribe)}
                            control={<Checkbox size="small" />}
                            label={
                                <Typography variant="body2" textTransform="capitalize">
                                    Email me about updates and special rewards
                                </Typography>
                            }
                            labelPlacement="end"
                            checked={acceptEmailSubscribe}
                        />
                        <FormControlLabel
                            value="end"
                            onChange={() => setAcceptTerms(!acceptTerms)}
                            control={<Checkbox size="small" />}
                            label={
                                <Typography variant="body2" textTransform="capitalize">
                                    Accept the
                                    <Link
                                        variant="body2"
                                        href={`${websiteURL}/eula`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ pl: 0.5 }}
                                    >
                                        Terms and Conditions
                                    </Link>
                                </Typography>
                            }
                            labelPlacement="end"
                            checked={acceptTerms}
                        />
                    </Stack>
                    <Button size="large" fullWidth disabled={!isFirstPageValid} onClick={onFirstPageSubmit}>
                        Sign Up
                    </Button>
                    <Stack direction="row" gap={2} justifyContent="center" alignItems="center">
                        <Typography variant="body2">Already have an account?</Typography>
                        <Link
                            variant="body2"
                            onClick={() => {
                                unregisterDialog(Dialogs.SIGNUP)
                                registerDialog(Dialogs.SIGNIN, <SignIn />)
                                analyticsManager.dispatchEvent('openLogin', {
                                    origin: 'signupDialog',
                                })
                            }}
                        >
                            Log In
                        </Link>
                    </Stack>
                </>
            ),
            gap: '8px',
            showBackArrow: false,
        },
        1: {
            title: 'Almost done!',
            content: (
                <>
                    <TextField
                        label="Pick a username:"
                        margin="normal"
                        type="text"
                        autoComplete="username"
                        value={username}
                        onChange={(e) => nameChangeHandler(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end" sx={{ px: 1, py: 2.7 }}>
                                    <IconButton edge="end" disabled>
                                        <UserIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        error={!isUsernameValid && isUsernameValid !== undefined}
                        helperText={usernameErrorMessage}
                        FormHelperTextProps={{
                            sx:
                                usernameCheckState === ValidationState.FREE
                                    ? { color: 'success.main' }
                                    : usernameCheckState === ValidationState.LOADING
                                      ? { color: 'text.primary' }
                                      : { color: 'error.main' },
                        }}
                    />
                    <CountrySelect label="Country:" value={country} onChange={setCountry} />
                    <Button
                        size="large"
                        fullWidth
                        onClick={onSecondPageSubmit}
                        disabled={!isSecondPageValid || isCompletingSignup}
                    >
                        Complete Sign Up
                    </Button>
                    {isSocialSignup && (
                        <Typography
                            variant="body2"
                            textTransform="capitalize"
                            textAlign="center"
                            sx={{ lineHeight: 1.5 }}
                        >
                            By signing up, I accept the site
                            <Link
                                href={`${websiteURL}/eula`}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ pl: 0.5 }}
                            >
                                Terms and Conditions
                            </Link>
                        </Typography>
                    )}
                </>
            ),
            gap: undefined,
            showBackArrow: !isSocialSignup,
        },
        2: {
            title: 'WELCOME TO THE CLUB!',
            content: (
                <>
                    <Typography textAlign="left" variant="body1" sx={{ width: '100%' }}>
                        Thank you for signing up!
                    </Typography>
                    {amount > 0 ? (
                        <Stack direction="row" gap={1} alignItems="center" sx={{ width: '100%' }}>
                            <Typography variant="body1">{`You Earned ${amount}x`}</Typography>
                            <HintBonusIcon adaptSize={false} />
                            <Typography variant="body1">Hints.</Typography>
                        </Stack>
                    ) : null}
                    <Typography textAlign="left" variant="body1" sx={{ width: '100%' }}>
                        Play puzzles and enjoy your new rewards!
                    </Typography>
                    <Button size="large" fullWidth onClick={onThirdPageSubmit}>
                        Let's go!
                    </Button>
                </>
            ),
            gap: '16px',
            showBackArrow: false,
        },
    }

    return (
        <BaseDialog
            name={isSocialSignup ? Dialogs.SIGN_UP_SOCIAL : Dialogs.SIGNUP}
            title={pages[currentPage].title}
            gap={pages[currentPage].gap}
            showBackArrow={pages[currentPage].showBackArrow}
            onBack={() => setCurrentPage(currentPage - 1)}
            onClose={onCloseHandler}
            forceBlock
        >
            {pages[currentPage].content}
        </BaseDialog>
    )
}

export default SignUp
